'use client'

import { useEffect, useRef } from 'react'
import { announce } from '@/lib/announce'
import { cn } from '@/lib/utils'
import { assessLength, formatSeconds, MAX_CLIP_SECONDS, MAX_SPOKEN_CHARS, type LengthState } from '@/lib/voice-length'

interface VoiceLengthMeterProps {
  text: string
  id: string
  /** The chosen voice's pace multiplier from the catalog. */
  pace?: number
}

const OVER = `That runs past ${MAX_CLIP_SECONDS} seconds. Cut it to ${MAX_SPOKEN_CHARS} characters or fewer.`
const TIGHT = `Close to the ${MAX_CLIP_SECONDS}-second limit. A slower voice may be cut off at the end.`
const TIGHT_PAST = `This voice is slow enough that this line may run past ${MAX_CLIP_SECONDS} seconds and lose its last words. Shorten it.`

function warningFor(state: LengthState, seconds: number): string | undefined {
  if (state === 'over') return OVER
  if (state === 'tight') return seconds > MAX_CLIP_SECONDS ? TIGHT_PAST : TIGHT
  return undefined
}

/**
 * A calibrated readout of how long the typed line will run, with the spoken
 * character count beside it. The estimate is the one in lib/voice-length.ts;
 * the real length is reported once the clip exists. The state is carried by
 * words and weight, never by colour alone.
 */
export function VoiceLengthMeter({ text, id, pace = 1 }: VoiceLengthMeterProps) {
  const { state, spokenChars, seconds } = assessLength(text, pace)
  const warning = warningFor(state, seconds)
  const emphasised = state === 'tight' || state === 'over'

  const previous = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (warning && previous.current !== warning) announce(warning, 'polite')
    previous.current = warning
  }, [warning])

  return (
    <div id={id}>
      <div className="flex items-baseline justify-between gap-4 text-sm tabular-nums">
        <p className={cn(emphasised ? 'font-semibold text-[var(--tls-fg-strong)]' : 'text-[var(--tls-fg-muted)]')}>
          {state === 'empty' ? `Up to ${MAX_CLIP_SECONDS} s of speech` : `About ${formatSeconds(seconds)} of ${MAX_CLIP_SECONDS} s`}
        </p>
        <p className={cn(emphasised ? 'font-semibold text-[var(--tls-fg-strong)]' : 'text-[var(--tls-fg-subtle)]')}>
          {spokenChars}/{MAX_SPOKEN_CHARS}
        </p>
      </div>
      <div aria-hidden="true" className="mt-2 h-1 bg-[var(--tls-border-subtle)]">
        <div
          className="h-full bg-[var(--tls-fg-strong)]"
          style={{ width: `${Math.min(100, (seconds / MAX_CLIP_SECONDS) * 100)}%` }}
        />
      </div>
      {warning && <p className="mt-2 text-sm font-medium text-[var(--tls-fg-strong)]">{warning}</p>}
    </div>
  )
}
